import type { ReactNode } from 'react';

import { cn } from '@/shared/lib/utils';
import { Button, type ButtonSize } from '@/shared/ui/button';

/** Jedna volba segmentovaného přepínače. */
export interface SegmentedButtonOption<T extends string> {
  value: T;
  label: ReactNode;
}

interface SegmentedButtonsCommon<T extends string> {
  options: ReadonlyArray<SegmentedButtonOption<T>>;
  disabled?: boolean;
  size?: ButtonSize;
  /** HTML title (tooltip) na skupině — např. důvod zamčení. */
  title?: string;
  className?: string;
}

export type SegmentedButtonsProps<T extends string> = SegmentedButtonsCommon<T> &
  (
    | { multiple?: false; value: T | null | undefined; onChange: (value: T) => void }
    | { multiple: true; value: readonly T[]; onChange: (value: T[]) => void }
  );

/**
 * Segmentovaný přepínač z tlačítek — aktivní volba je <b>primary</b>, ostatní <b>outline</b>.
 * Sjednocený vzhled toggle přepínačů napříč formuláři (single i multi výběr). Prezentační
 * komponenta: hodnotu i změnu drží volající (vhodné pod TanStack Form field adaptér).
 */
export function SegmentedButtons<T extends string>(props: SegmentedButtonsProps<T>) {
  const { options, disabled, size = 'sm', title, className } = props;

  const isActive = (value: T): boolean =>
    props.multiple ? props.value.includes(value) : props.value === value;

  const handleClick = (value: T): void => {
    if (props.multiple) {
      const selected = new Set(props.value);
      if (selected.has(value)) {
        selected.delete(value);
      } else {
        selected.add(value);
      }
      // Zachovej pořadí dle `options`.
      props.onChange(options.map((option) => option.value).filter((v) => selected.has(v)));
    } else {
      props.onChange(value);
    }
  };

  return (
    <div className={cn('flex flex-wrap gap-1', className)} role="group" title={title}>
      {options.map((option) => (
        <Button
          key={option.value}
          type="button"
          size={size}
          variant={isActive(option.value) ? 'primary' : 'outline'}
          disabled={disabled}
          onClick={() => handleClick(option.value)}
        >
          {option.label}
        </Button>
      ))}
    </div>
  );
}
